import React, { useEffect, useState, useRef } from 'react';
import { BsFillCalendarFill } from 'react-icons/bs';
import { FaTractor } from 'react-icons/fa';
import { SCOPES } from '../../constants/scopes';
import useSearchParamsWrapper from '../../hooks/useSearchParamsWrapper';
import Spinner from '../../media/spinner.svg';
import {
  COVER_CROP_CODES,
  COVER_CROP_TYPES,
  FILTERS,
  getCoverCropTypeByCode,
  getSeedDateTypeByCode,
  HARVEST_DATE_CODES,
  SEED_DATE_CODES,
  SEED_DATE_TYPES,
} from '../Field/constants/filters';
import ErrorHandler from '../Shared/ErrorHandler/ErrorHandler';
import FieldLabel from '../Shared/FieldLabel/FieldLabel';
import Filter from '../Shared/Filter/Filter';
import useFilters from '../Shared/Filter/useFilters';
import Label from '../Shared/Label/Label';
import Pagination from '../Shared/Pagination/Pagination';
import PlaceSearch from '../Shared/PlaceSearch/PlaceSearch';
import RadioButton from '../Shared/RadioButton/RadioButton';
import { FETCH_STATUSES } from './constants/consts';
import FieldCard from './FieldCard';
import { FieldSearchWrap } from './fields.styles';
import FieldsMap from './FieldsMap';
import Proj from 'proj4';
import { PanelLeft } from '../Shared/PanelLeft/PanelLeft';
import { t } from 'ttag';
import api from '../../api/api';
import useSoilTypes, { SOIL_TYPE_CODES } from '../../hooks/useSoilTypes';
import useCropTypes, { CROP_TYPE_CODES } from '../../hooks/useCropTypes';
import { parseCodeFromId, parseFullYearFromScope } from '../../utils/utils';
import usePreCropTypes, { PRE_CROP_CODES } from '../../hooks/usePreCropTypes';

export default function FieldsContainer() {
  const { getSearchParams, addSearchParams } = useSearchParamsWrapper();
  const [fields, setFields] = useState([]);
  const [fieldsStatus, setFieldsStatus] = useState(FETCH_STATUSES.loading);
  const signalRef = useRef();

  const [page, setPage] = useState(() => {
    const { page } = getSearchParams();
    return page;
  });
  const [numberOfFields, setNumberOfFields] = useState(1);

  const [bbox, setBBox] = useState(() => {
    const { _southWestLat, _southWestLng, _northEastLat, _northEastLng } = getSearchParams();
    return {
      bounds: {
        _northEast: {
          lng: parseFloat(_northEastLng),
          lat: parseFloat(_northEastLat),
        },
        _southWest: {
          lng: parseFloat(_southWestLng),
          lat: parseFloat(_southWestLat),
        },
      },
      wasUserDrag: false,
    };
  });

  const MAX_RESULTS_ON_MAP = 1000;
  const RESULTS_PER_PAGE = 10;
  const DEFAULT_ZOOM_LEVEL = 15;

  const [zoom, setZoom] = useState(DEFAULT_ZOOM_LEVEL);
  const {
    handleFilterChange,
    handleFilterToggleClick,
    handleFilterCloseClick,
    handleFilterSaveClick,
    activeFilter,
    savedFilters,
    unsavedFilters,
    setUnsavedAndSavedFilter,
  } = useFilters(getFilters());

  function getFilters() {
    const { cropType, year, mapLayer, soilType, seedDate, harvestDate, coverCrop, preCrop } =
      getSearchParams();
    return {
      cropType,
      year,
      mapLayer,
      soilType,
      seedDate,
      harvestDate,
      coverCrop,
      preCrop,
    };
  }

  const { soilTypes, getSoilTypeNameById, getSoilTypeNameByCode } = useSoilTypes(savedFilters.year);
  const { cropTypes, getCropTypeNameByCode } = useCropTypes(savedFilters.year);
  const { preCropTypes } = usePreCropTypes(savedFilters.year);

  useEffect(() => {
    initialiseFields();

    return () => {
      signalRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilters]);

  useEffect(() => {
    const { _northEast, _southWest } = bbox.bounds;
    addSearchParams({
      page,
      ...savedFilters,
      _northEastLng: _northEast.lng,
      _northEastLat: _northEast.lat,
      _southWestLng: _southWest.lng,
      _southWestLat: _southWest.lat,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, savedFilters, bbox]);

  function createAreaMonitoringBBoxFromLeafletBBox(leafletBBox) {
    const bbox3857 = Object.keys(leafletBBox).map((bound) => {
      const lngLat = [leafletBBox[bound].lng, leafletBBox[bound].lat];
      return Proj('EPSG:4326', 'EPSG:3857', lngLat);
    });

    const areaMonitoringBBox = [bbox3857[1][0], bbox3857[1][1], bbox3857[0][0], bbox3857[0][1]];
    return areaMonitoringBBox.join(',');
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function initialiseFields(customBBox) {
    if (signalRef.current) {
      signalRef.current.abort();
    }
    signalRef.current = new AbortController();
    setFields([]);
    setFieldsStatus(FETCH_STATUSES.loading);
    try {
      const bbox4326 = customBBox?.bounds ?? bbox.bounds;
      const bbox3857 = createAreaMonitoringBBoxFromLeafletBBox(bbox4326);
      const { data: amountOfFields } = await getNumberOfFields(bbox3857);

      if (amountOfFields !== numberOfFields) {
        setPage(1);
      }

      const fields = await getFields(bbox3857);

      setNumberOfFields(amountOfFields);
      setFields(fields);
      setFieldsStatus(FETCH_STATUSES.success);
    } catch (error) {
      if (error.message !== 'canceled') {
        setFieldsStatus(FETCH_STATUSES.error);
      }
    }
  }

  async function getFields(bbox) {
    const { cropType, year, soilType, seedDate, harvestDate, coverCrop, preCrop } = savedFilters;
    let cropId = `${year}.CL_CROP.${cropType}`;

    const parsedBbox = bbox.split(',').map((coord) => parseFloat(coord));
    const center = [(parsedBbox[0] + parsedBbox[2]) / 2, (parsedBbox[1] + parsedBbox[3]) / 2];
    const centerString = center.join(' ');

    let filter = `bbox(${bbox})`;
    if (cropType !== CROP_TYPE_CODES.ALL) {
      filter = `${filter} and cropId="${cropId}"`;
    }
    if (soilType !== SOIL_TYPE_CODES.ALL) {
      filter = `${filter} and soilTypeId="${year}.CL_ST.${soilType}"`;
    }
    if (seedDate !== SEED_DATE_CODES.ALL) {
      filter = `${filter} and attributes.att_seeddate_class="${seedDate}"`;
    }
    if (harvestDate !== HARVEST_DATE_CODES.ALL) {
      filter = `${filter} and attributes.att_harvdate_class="${harvestDate}"`;
    }
    if (coverCrop !== COVER_CROP_CODES.ALL) {
      filter = `${filter} and attributes.att_covercrop="${coverCrop}"`;
    }
    if (preCrop !== PRE_CROP_CODES.ALL) {
      filter = `${filter} and attributes.att_precrop="${preCrop}"`;
    }

    const queryArguments = [
      `filter=${filter}`,
      `size=${MAX_RESULTS_ON_MAP}`,
      `sort=distance(geometry)`,
      `fromGeometry="SRID=3857;Point(${centerString})"`,
    ];

    const { signal } = signalRef.current;

    return await api().fetchFields(year, queryArguments, signal);
  }

  async function getNumberOfFields(bbox) {
    const { cropType, year, soilType, seedDate, harvestDate, coverCrop, preCrop } = savedFilters;

    let cropId = `${year}.CL_CROP.${cropType}`;

    let filter = `bbox(${bbox})`;
    if (cropType !== CROP_TYPE_CODES.ALL) {
      filter = `${filter} and cropId="${cropId}"`;
    }
    if (soilType !== SOIL_TYPE_CODES.ALL) {
      filter = `${filter} and soilTypeId="${year}.CL_ST.${soilType}"`;
    }
    if (seedDate !== SEED_DATE_CODES.ALL) {
      filter = `${filter} and attributes.att_seeddate_class="${seedDate}"`;
    }
    if (harvestDate !== HARVEST_DATE_CODES.ALL) {
      filter = `${filter} and attributes.att_harvdate_class="${harvestDate}"`;
    }
    if (coverCrop !== COVER_CROP_CODES.ALL) {
      filter = `${filter} and attributes.att_covercrop="${coverCrop}"`;
    }
    if (preCrop !== PRE_CROP_CODES.ALL) {
      filter = `${filter} and attributes.att_precrop="${preCrop}"`;
    }

    const { signal } = signalRef.current;
    return await api().fetchNumberOfFields(year, filter, signal);
  }

  function handleResultClick(result) {
    let { bbox, center } = result;

    if (!bbox) {
      bbox = [center[0], center[1], center[0], center[1]];
    }

    const leafletBBox = {
      _northEast: {
        lat: bbox[1],
        lng: bbox[0],
      },
      _southWest: {
        lat: bbox[3],
        lng: bbox[2],
      },
    };
    setBBox({ bounds: leafletBBox, wasUserDrag: false });
    initialiseFields({ bounds: leafletBBox });
  }

  function paginate(array, pageSize, pageNumber) {
    return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  }

  function resetFilters() {
    setUnsavedAndSavedFilter(FILTERS.year, SCOPES[SCOPES.length - 1].value);
    setUnsavedAndSavedFilter(FILTERS.cropType, CROP_TYPE_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.soilType, SOIL_TYPE_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.seedDate, SEED_DATE_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.harvestDate, HARVEST_DATE_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.coverCrop, COVER_CROP_CODES.ALL);
    setUnsavedAndSavedFilter(FILTERS.preCrop, PRE_CROP_CODES.ALL);
  }

  function renderFields(fields) {
    if (fieldsStatus === FETCH_STATUSES.loading) {
      return (
        <div className="spinner-wrap">
          <img src={Spinner} alt="" />
        </div>
      );
    }

    if (fieldsStatus === FETCH_STATUSES.error) {
      return (
        <ErrorHandler
          title={t`Error`}
          description={t`Something went wrong when fetching fields`}
          onTryAgainClick={() => {
            initialiseFields();
          }}
        />
      );
    }
    if (fields.length === 0) {
      return (
        <ErrorHandler
          title={t`No fields found`}
          description={t`There are no fields in this area. Try a new location and search again`}
          onTryAgainClick={() => initialiseFields()}
          buttonText={t`Search`}
        />
      );
    }
    return (
      <div>
        {paginate(fields, RESULTS_PER_PAGE, page).map((field) => {
          return (
            <FieldCard
              year={savedFilters.year}
              field={field}
              id={field.id}
              geometry={field.geometry}
              onZoomToFieldClick={(geometry) => setBBox(geometry)}
              key={field.id}
              cropType={getCropTypeNameByCode(parseCodeFromId(field.cropId))}
              soilName={getSoilTypeNameById(field.soilTypeId)}
            />
          );
        })}
        <Pagination
          resultsPerPage={RESULTS_PER_PAGE}
          currentPage={page}
          onChangePageClick={(page) => {
            setPage(page);
          }}
          numberOfResults={fields.length}
        />
      </div>
    );
  }
  return (
    <FieldSearchWrap>
      <PanelLeft>
        <PlaceSearch onResultClick={handleResultClick} />
        <div className="field-filters-wrap">
          <Label>
            {t`Filter by`} (<span className="reset" onClick={resetFilters}>{t`Reset filters`}</span>)
          </Label>
          <div className="field-filters">
            <Filter
              onOutsideClick={() => handleFilterCloseClick(FILTERS.year)}
              onSaveChangesClick={() => handleFilterSaveClick(FILTERS.year)}
              onToggleClick={() => {
                handleFilterToggleClick(FILTERS.year);
              }}
              onCloseClick={() => handleFilterCloseClick(FILTERS.year)}
              isOpen={activeFilter === FILTERS.year}
              label={t`Year`}
            >
              {SCOPES.map(({ year, value }) => (
                <RadioButton
                  key={value}
                  onChange={(event) => {
                    handleFilterChange(FILTERS.year, event.currentTarget.value);
                  }}
                  label={year}
                  value={value}
                  checked={unsavedFilters.year === value}
                />
              ))}
            </Filter>
            <Filter
              onOutsideClick={() => handleFilterCloseClick(FILTERS.cropType)}
              onSaveChangesClick={() => handleFilterSaveClick(FILTERS.cropType)}
              onToggleClick={() => handleFilterToggleClick(FILTERS.cropType)}
              onCloseClick={() => handleFilterCloseClick(FILTERS.cropType)}
              isOpen={activeFilter === FILTERS.cropType}
              label={t`Crop type`}
            >
              {cropTypes.map((crop) => (
                <RadioButton
                  key={crop.code}
                  onChange={(event) => {
                    handleFilterChange(FILTERS.cropType, event.currentTarget.value);
                  }}
                  value={crop.code}
                  checked={unsavedFilters.cropType === crop.code}
                  label={crop.names.de.toLowerCase()}
                />
              ))}
            </Filter>
            <Filter
              onOutsideClick={() => handleFilterCloseClick(FILTERS.soilType)}
              onSaveChangesClick={() => handleFilterSaveClick(FILTERS.soilType)}
              onToggleClick={() => handleFilterToggleClick(FILTERS.soilType)}
              onCloseClick={() => handleFilterCloseClick(FILTERS.soilType)}
              isOpen={activeFilter === FILTERS.soilType}
              label={t`Soil type`}
            >
              {soilTypes.map((soil) => (
                <RadioButton
                  key={soil.code}
                  onChange={(event) => {
                    handleFilterChange(FILTERS.soilType, event.currentTarget.value);
                  }}
                  value={soil.code}
                  checked={unsavedFilters.soilType === soil.code}
                  label={soil.names.de.toLowerCase()}
                />
              ))}
            </Filter>
            <Filter
              onOutsideClick={() => handleFilterCloseClick(FILTERS.coverCrop)}
              onSaveChangesClick={() => handleFilterSaveClick(FILTERS.coverCrop)}
              onToggleClick={() => handleFilterToggleClick(FILTERS.coverCrop)}
              onCloseClick={() => handleFilterCloseClick(FILTERS.coverCrop)}
              isOpen={activeFilter === FILTERS.coverCrop}
              label={t`Cover crop`}
            >
              {COVER_CROP_TYPES.map((coverCrop) => (
                <RadioButton
                  key={coverCrop.code}
                  onChange={(event) => {
                    handleFilterChange(FILTERS.coverCrop, event.currentTarget.value);
                  }}
                  value={coverCrop.code}
                  checked={unsavedFilters.coverCrop === coverCrop.code}
                  label={coverCrop.names.de.toLowerCase()}
                />
              ))}
            </Filter>
            <Filter
              onOutsideClick={() => handleFilterCloseClick(FILTERS.preCrop)}
              onSaveChangesClick={() => handleFilterSaveClick(FILTERS.preCrop)}
              onToggleClick={() => handleFilterToggleClick(FILTERS.preCrop)}
              onCloseClick={() => handleFilterCloseClick(FILTERS.preCrop)}
              isOpen={activeFilter === FILTERS.preCrop}
              label={t`Pre crop`}
            >
              {preCropTypes.map((crop) => (
                <RadioButton
                  key={crop.code}
                  onChange={(event) => {
                    handleFilterChange(FILTERS.preCrop, event.currentTarget.value);
                  }}
                  value={crop.code}
                  checked={unsavedFilters.preCrop === crop.code}
                  label={crop.names.de.toLowerCase()}
                />
              ))}
            </Filter>
            <Filter
              onOutsideClick={() => handleFilterCloseClick(FILTERS.seedDate)}
              onSaveChangesClick={() => handleFilterSaveClick(FILTERS.seedDate)}
              onToggleClick={() => handleFilterToggleClick(FILTERS.seedDate)}
              onCloseClick={() => handleFilterCloseClick(FILTERS.seedDate)}
              isOpen={activeFilter === FILTERS.seedDate}
              label={t`Seed date`}
            >
              {SEED_DATE_TYPES.map((seedDate) => (
                <RadioButton
                  key={seedDate.code}
                  onChange={(event) => {
                    handleFilterChange(FILTERS.seedDate, event.currentTarget.value);
                  }}
                  value={seedDate.code}
                  checked={unsavedFilters.seedDate === seedDate.code}
                  label={seedDate.names.de.toLowerCase()}
                />
              ))}
            </Filter>
          </div>
        </div>
        <div className="fields-information">
          <div className="fields-information-number-of-fields">{t`Displaying ${fields.length} fields`}</div>
          <div className="field-information-stats">
            <div className="field-information-stat-wrap">
              <FieldLabel
                label={t`Crop type`}
                icon={<FaTractor />}
                value={getCropTypeNameByCode(savedFilters.cropType)}
              />
            </div>
            <FieldLabel
              label={t`Date`}
              icon={<BsFillCalendarFill />}
              value={`${parseFullYearFromScope(savedFilters.year)}`}
            />
            <FieldLabel label={t`Soil type`} value={getSoilTypeNameByCode(savedFilters.soilType)} />
            <FieldLabel label={t`Cover crop`} value={getCoverCropTypeByCode(savedFilters.coverCrop)} />
            <FieldLabel label={t`Pre crop`} value={getCropTypeNameByCode(savedFilters.preCrop)} />
            <FieldLabel label={t`Seed date`} value={getSeedDateTypeByCode(savedFilters.seedDate)} />
          </div>
        </div>
        <div className="field-card-list">{renderFields(fields)}</div>
      </PanelLeft>
      <FieldsMap
        bbox={bbox.bounds}
        wasUserDrag={bbox.wasUserDrag}
        zoom={zoom}
        activeFilter={activeFilter}
        fieldsStatus={fieldsStatus}
        mapLayer={savedFilters.mapLayer}
        onFilterCloseClick={() => handleFilterCloseClick(FILTERS.mapLayer)}
        onOutsideFilterClick={() => handleFilterCloseClick(FILTERS.mapLayer)}
        onToggleFilterClick={() => handleFilterToggleClick(FILTERS.mapLayer)}
        onMapLayerChange={(layer) => handleFilterSaveClick(layer)}
        onZoomChange={(zoom) => setZoom(zoom)}
        onSearchClick={(bbox) => {
          setBBox({ bounds: bbox, wasUserDrag: false });
          setPage(1);
          initialiseFields({ bounds: bbox });
        }}
        onFilterChangeClick={(mapLayer) => handleFilterChange(FILTERS.mapLayer, mapLayer, true)}
        fields={fields}
        scope={savedFilters.year}
        onMoveEnd={(bbox) => {
          setBBox({ bounds: bbox, wasUserDrag: true });
        }}
      />
    </FieldSearchWrap>
  );
}
